import { CalendarDays, CheckCircle2, Clock } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { formatDayShort, formatDayNumber, formatDate } from "@/lib/dateFormatters";
import { translateTaskTitle } from "@/lib/taskTitle";

/**
 * La tarjeta de un día del Planificador, tal cual, para poder usarla también en
 * el portal del trabajador.
 *
 * Se sacó aquí porque el trabajador y el encargado estaban mirando dos diseños
 * distintos del mismo día: cuando el trabajador terminaba todo, su pantalla se
 * quedaba prácticamente vacía. Ahora los dos ven lo mismo — la tarea completada
 * sigue en su sitio, tachada y con el visto verde — y al estar en un solo
 * archivo no pueden volver a separarse.
 */

export interface DayCardTask {
  id: string;
  title: string;
  scheduled_date: string;
  status: string | null;
  greenhouses?: { name: string } | null;
  blocks?: { name: string } | null;
}

/** El círculo de estado de la derecha. Mismos colores que en el Planificador. */
export function taskStatusCircle(status: string | null): string {
  switch (status) {
    case "completed":
      return "border-emerald-500 bg-emerald-500 text-white";
    case "in_progress":
      return "border-sky-500 bg-sky-500 text-white";
    case "pending":
      return "border-amber-500 bg-amber-500 text-white";
    default:
      return "border-slate-300 bg-slate-200 text-slate-500";
  }
}

/** Cabecera del día: «Vie · 31 Jul». */
export function formatDayHeader(day: Date): string {
  const dayName = formatDayShort(day);
  const dayNum = formatDayNumber(day);
  const month = formatDate(day, "MMM").replace(".", "");
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return `${cap(dayName)} · ${dayNum} ${cap(month)}`;
}

export function DayTaskRow({
  task,
  onClick,
}: {
  task: DayCardTask;
  onClick?: (task: DayCardTask) => void;
}) {
  const { t } = useTranslation();
  const isDone = task.status === "completed";
  const loc =
    task.greenhouses?.name && task.blocks?.name
      ? `${task.greenhouses.name} · ${task.blocks.name}`
      : task.greenhouses?.name || task.blocks?.name || "";
  let time: string | null = null;
  try {
    time = format(new Date(task.scheduled_date), "HH:mm");
  } catch {
    time = null;
  }
  const meta = [time, loc].filter(Boolean).join(" · ") || "—";

  return (
    <div
      onClick={() => onClick?.(task)}
      className={cn(
        "relative overflow-hidden rounded-[14px] border border-[#b6dded]/60 bg-white shadow-sm transition-all py-2.5 pl-3.5 pr-2.5",
        onClick && "cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99]",
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "text-sm font-semibold leading-tight truncate",
              isDone ? "line-through text-foreground/45" : "text-foreground",
            )}
          >
            {translateTaskTitle(task.title, t)}
          </p>
          <div className="flex items-center gap-1 mt-0.5 text-[11.5px] font-['Mulish'] font-semibold text-muted-foreground min-w-0">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{meta}</span>
          </div>
        </div>
        <span
          className={cn(
            "flex items-center justify-center w-[26px] h-[26px] rounded-full flex-shrink-0 border-[1.5px]",
            taskStatusCircle(task.status),
          )}
        >
          {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
        </span>
      </div>
    </div>
  );
}

/**
 * Tarjeta completa de un día: cabecera con la fecha, píldora «Hoy» y las tareas.
 * `className` la deja adaptarse: el Planificador la usa con ancho fijo y el
 * portal del trabajador a todo el ancho.
 */
export function DayTaskCard({
  day,
  tasks,
  isToday = false,
  onTaskClick,
  emptyLabel,
  className,
  minBodyHeight,
}: {
  day: Date;
  tasks: DayCardTask[];
  isToday?: boolean;
  onTaskClick?: (task: DayCardTask) => void;
  emptyLabel?: string;
  className?: string;
  /** Alto mínimo del cuerpo. Sin él, un día con una sola tarea queda muy bajo. */
  minBodyHeight?: string;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "rounded-2xl border border-[#b6dded] bg-white shadow-sm p-4",
        className,
      )}
    >
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarDays className="w-4 h-4 flex-shrink-0 text-foreground" />
          <p className="text-sm md:text-base font-bold leading-tight truncate text-foreground">
            {formatDayHeader(day)}
          </p>
        </div>
        {isToday ? (
          <span className="text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-xl bg-[#2B7DE0] text-white flex-shrink-0">
            {t("planner.view.today")}
          </span>
        ) : tasks.length > 0 ? (
          <span className="text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-xl bg-primary/10 text-primary flex-shrink-0">
            {tasks.length}
          </span>
        ) : null}
      </div>

      <div style={minBodyHeight ? { minHeight: minBodyHeight } : undefined}>
        {tasks.length === 0 ? (
          <div className="w-full h-full min-h-[120px] flex flex-col items-center justify-center gap-2 rounded-2xl text-muted-foreground/40">
            <span className="text-sm font-medium">
              {emptyLabel ?? t("planner.noTasks", "Sin tareas")}
            </span>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map((task) => (
              <DayTaskRow key={task.id} task={task} onClick={onTaskClick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
